/**
 * Timeline des programmes — packs de SKILLCARDS (migration 0289, lot F).
 *
 * - Lecture publique : RPC `get_curriculum_timeline` (packs PUBLIÉS).
 * - Élève : RPC `get_my_progression` (% maîtrisé + HORA par pack).
 * - Prof : RPC `get_class_progression`, `set_class_progression`,
 *   `assign_pack_to_class` ; « Revenir à la progression type » = suppression
 *   directe des surcharges de la classe (RLS tutor_all).
 * - Admin : tables `curriculum_packs` / `_chapters` / `_cards` en direct (RLS
 *   admin) + edge fn `curriculum-pack-plan` (brouillon IA).
 *
 * Tables postérieures aux types générés → client générique.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { ClassPackOverride } from '@jeraisonne/db/curriculum';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export interface PackCardRef {
  id: string;
  short_code: string;
  title: string;
  rarity?: string | null;
}

export interface TimelinePackRow {
  id: string;
  slug: string;
  level: string;
  subject: string;
  title: string;
  hook: string;
  start_month: number;
  position: number;
  color: string | null;
  icon: string;
  /** Fin indicative dans l'année (planning, migration 0292). */
  end_month: number | null;
  /** Programme officiel du pack (en vigueur, puisque la timeline ne renvoie que ceux-là). */
  programme: ProgrammeRef | null;
  cards: PackCardRef[];
  chapters: { id: string; title: string; slug: string }[];
}

export interface ProgrammeRef {
  id: string;
  title: string;
  source: string | null;
  valid_from: string;
  valid_until: string | null;
}

export interface MyProgressionPack {
  id: string;
  slug: string;
  subject: string;
  title: string;
  hook: string;
  start_month: number;
  end_month: number | null;
  position: number;
  color: string | null;
  icon: string;
  card_count: number;
  mastered_count: number;
  hora: number;
}

export interface MyProgression {
  level: string | null;
  packs: MyProgressionPack[];
}

export interface ClassProgressionPack {
  id: string;
  slug: string;
  subject: string;
  title: string;
  hook: string;
  start_month: number;
  default_start_month: number;
  end_month: number | null;
  position: number;
  color: string | null;
  icon: string;
  hidden: boolean;
  overridden: boolean;
  card_count: number;
}

export interface ClassProgressionStudent {
  id: string;
  first_name: string;
  avatar_url: string | null;
  packs: Record<string, { mastered: number; hora: number; assigned: number }>;
}

export interface ClassProgression {
  class: { id: string; name: string; level: string | null; subject: string | null };
  packs: ClassProgressionPack[];
  students: ClassProgressionStudent[];
}

export const curriculumTimelineKey = (level?: string, subject?: string) =>
  ['curriculum-timeline', level ?? null, subject ?? null] as const;

export function useCurriculumTimeline(
  level?: string | null,
  subject?: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: curriculumTimelineKey(level ?? undefined, subject ?? undefined),
    enabled,
    queryFn: async (): Promise<TimelinePackRow[]> => {
      const { data, error } = await db.rpc('get_curriculum_timeline', {
        p_level: level ?? null,
        p_subject: subject ?? null,
      });
      if (error) throw new Error(error.message);
      return (data ?? []) as TimelinePackRow[];
    },
  });
}

export const myProgressionKey = ['my-progression'] as const;

export function useMyProgression(enabled = true) {
  return useQuery({
    queryKey: myProgressionKey,
    enabled,
    queryFn: async (): Promise<MyProgression> => {
      const { data, error } = await db.rpc('get_my_progression');
      if (error) throw new Error(error.message);
      const raw = (data ?? {}) as Partial<MyProgression>;
      return { level: raw.level ?? null, packs: raw.packs ?? [] };
    },
  });
}

export const classProgressionKey = (classId: string) => ['class-progression', classId] as const;

export function useClassProgression(classId: string) {
  return useQuery({
    queryKey: classProgressionKey(classId),
    queryFn: async (): Promise<ClassProgression> => {
      const { data, error } = await db.rpc('get_class_progression', { p_class_id: classId });
      if (error) throw new Error(error.message);
      return data as ClassProgression;
    },
  });
}

export function useSetClassProgression(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: ClassPackOverride[]) => {
      const { error } = await db.rpc('set_class_progression', {
        p_class_id: classId,
        p_items: items,
      });
      if (error) throw new Error(error.message);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: classProgressionKey(classId) }),
  });
}

export function useResetClassProgression(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await db.from('class_progressions').delete().eq('class_id', classId);
      if (error) throw new Error(error.message);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: classProgressionKey(classId) }),
  });
}

const ASSIGN_ERRORS: Record<string, string> = {
  pack_not_published: 'Ce pack n’est pas encore publié.',
  programme_not_active: 'Ce pack appartient à un programme qui n’est pas en vigueur aujourd’hui.',
  not_authorized: 'Seul le prof de la classe peut assigner un pack.',
};

export function translateAssignError(message: string): string {
  const key = Object.keys(ASSIGN_ERRORS).find((k) => message.includes(k));
  return key ? (ASSIGN_ERRORS[key] as string) : message;
}

export function useAssignPack(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (packId: string): Promise<number> => {
      const { data, error } = await db.rpc('assign_pack_to_class', {
        p_class_id: classId,
        p_pack_id: packId,
      });
      if (error) throw new Error(translateAssignError(error.message));
      return Number((data as { assigned?: number } | null)?.assigned ?? 0);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: classProgressionKey(classId) }),
  });
}

// ── Admin ───────────────────────────────────────────────────────────────────

export type PackStatus = 'draft' | 'published' | 'archived';

export interface AdminPack {
  id: string;
  slug: string;
  level: string;
  subject: string;
  title: string;
  hook: string;
  start_month: number;
  position: number;
  color: string | null;
  icon: string;
  status: PackStatus;
  /** Programme officiel du pack (migration 0292). */
  programme_id: string;
  /** Fin indicative dans l'année (planning). */
  end_month: number | null;
  missing_cards: { title: string; topic?: string }[];
  chapter_ids: string[];
  cards: PackCardRef[];
}

interface RawAdminPack extends Omit<AdminPack, 'chapter_ids' | 'cards'> {
  curriculum_pack_chapters: { chapter_id: string; position: number }[];
  curriculum_pack_cards: {
    position: number;
    cards: { id: string; short_code: string; title: string } | null;
  }[];
}

export const adminPacksKey = (level: string, subject: string) =>
  ['admin-packs', level, subject] as const;

// ── Programmes officiels (validité, migration 0292) ─────────────────────────

export interface Programme extends ProgrammeRef {
  level: string;
  subject: string;
  /** « Couverture du programme » affichée sur le site (migration 0507). */
  coverage_md?: string | null;
  /** Posée par trigger quand le texte change. */
  coverage_updated_at?: string | null;
}

export const programmesKey = (level: string, subject: string) =>
  ['curriculum-programmes', level, subject] as const;

export function useProgrammes(level: string, subject: string) {
  return useQuery({
    queryKey: programmesKey(level, subject),
    queryFn: async (): Promise<Programme[]> => {
      const { data, error } = await db
        .from('curriculum_programmes')
        .select(
          'id, level, subject, title, source, valid_from, valid_until, coverage_md, coverage_updated_at',
        )
        .eq('level', level)
        .eq('subject', subject)
        .order('valid_from', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as Programme[];
    },
  });
}

export type ProgrammeInput = Pick<
  Programme,
  'level' | 'subject' | 'title' | 'source' | 'valid_from' | 'valid_until' | 'coverage_md'
>;

const PROGRAMME_ERRORS: Record<string, string> = {
  curriculum_programmes_unique:
    'Un programme commence déjà à cette date pour ce niveau et cette matière.',
  curriculum_programmes_period_chk: 'La date de fin doit suivre la date de rentrée.',
};

export function translateProgrammeError(message: string): string {
  const key = Object.keys(PROGRAMME_ERRORS).find((k) => message.includes(k));
  return key ? (PROGRAMME_ERRORS[key] as string) : message;
}

export function useSaveProgramme(level: string, subject: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: ProgrammeInput }): Promise<string> => {
      if (id) {
        const { error } = await db.from('curriculum_programmes').update(input).eq('id', id);
        if (error) throw new Error(translateProgrammeError(error.message));
        return id;
      }
      const { data, error } = await db
        .from('curriculum_programmes')
        .insert(input)
        .select('id')
        .single();
      if (error) throw new Error(translateProgrammeError(error.message));
      return (data as { id: string }).id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: programmesKey(level, subject) });
      void qc.invalidateQueries({ queryKey: ['curriculum-timeline'] });
    },
  });
}

export function useAdminPacks(level: string, subject: string) {
  return useQuery({
    queryKey: adminPacksKey(level, subject),
    queryFn: async (): Promise<AdminPack[]> => {
      const { data, error } = await db
        .from('curriculum_packs')
        .select(
          'id, slug, level, subject, title, hook, start_month, position, color, icon, status, programme_id, end_month, missing_cards, curriculum_pack_chapters(chapter_id, position), curriculum_pack_cards(position, cards(id, short_code, title))',
        )
        .eq('level', level)
        .eq('subject', subject);
      if (error) throw new Error(error.message);
      return ((data ?? []) as unknown as RawAdminPack[]).map(
        ({ curriculum_pack_chapters, curriculum_pack_cards, ...p }) => ({
          ...p,
          missing_cards: Array.isArray(p.missing_cards) ? p.missing_cards : [],
          chapter_ids: [...curriculum_pack_chapters]
            .sort((a, b) => a.position - b.position)
            .map((c) => c.chapter_id),
          cards: [...curriculum_pack_cards]
            .sort((a, b) => a.position - b.position)
            .flatMap((c) => (c.cards ? [c.cards] : [])),
        }),
      );
    },
  });
}

export interface ChapterOption {
  id: string;
  slug: string;
  title: string;
}

export function useLevelChapters(level: string, subject: string) {
  return useQuery({
    queryKey: ['level-chapters', level, subject],
    queryFn: async (): Promise<ChapterOption[]> => {
      const { data, error } = await db
        .from('chapters')
        .select('id, slug, title, display_order')
        .eq('level', level)
        .eq('subject', subject)
        .neq('status', 'archived')
        .order('display_order');
      if (error) throw new Error(error.message);
      return ((data ?? []) as ChapterOption[]).filter(
        (c) => !c.slug.startsWith('obs-') && !c.slug.startsWith('summer-'),
      );
    },
  });
}

export type PackInput = Pick<
  AdminPack,
  | 'level'
  | 'subject'
  | 'title'
  | 'hook'
  | 'start_month'
  | 'position'
  | 'color'
  | 'icon'
  | 'status'
  | 'programme_id'
  | 'end_month'
> & { slug: string; missing_cards: AdminPack['missing_cards'] };

function invalidateAdmin(qc: ReturnType<typeof useQueryClient>, level: string, subject: string) {
  void qc.invalidateQueries({ queryKey: adminPacksKey(level, subject) });
  void qc.invalidateQueries({ queryKey: ['curriculum-timeline'] });
}

/** Crée ou met à jour un pack, puis remplace ses chapitres et ses cartes. */
export function useSavePack(level: string, subject: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
      chapterIds,
      cardIds,
    }: {
      id?: string;
      input: PackInput;
      chapterIds: string[];
      cardIds: string[];
    }): Promise<string> => {
      let packId = id;
      if (packId) {
        const { error } = await db.from('curriculum_packs').update(input).eq('id', packId);
        if (error) throw new Error(error.message);
      } else {
        const { data, error } = await db
          .from('curriculum_packs')
          .insert(input)
          .select('id')
          .single();
        if (error) throw new Error(error.message);
        packId = (data as { id: string }).id;
      }
      const delCh = await db.from('curriculum_pack_chapters').delete().eq('pack_id', packId);
      if (delCh.error) throw new Error(delCh.error.message);
      if (chapterIds.length) {
        const ins = await db
          .from('curriculum_pack_chapters')
          .insert(
            chapterIds.map((chapter_id, position) => ({ pack_id: packId, chapter_id, position })),
          );
        if (ins.error) throw new Error(ins.error.message);
      }
      const delCards = await db.from('curriculum_pack_cards').delete().eq('pack_id', packId);
      if (delCards.error) throw new Error(delCards.error.message);
      if (cardIds.length) {
        const ins = await db
          .from('curriculum_pack_cards')
          .insert(cardIds.map((card_id, position) => ({ pack_id: packId, card_id, position })));
        if (ins.error) throw new Error(ins.error.message);
      }
      return packId!;
    },
    onSuccess: () => invalidateAdmin(qc, level, subject),
  });
}

export function useSetPackStatus(level: string, subject: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: PackStatus }) => {
      const { error } = await db.from('curriculum_packs').update({ status }).in('id', ids);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateAdmin(qc, level, subject),
  });
}

export function useDeletePack(level: string, subject: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.from('curriculum_packs').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateAdmin(qc, level, subject),
  });
}

export function useGeneratePackPlan(level: string, subject: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      instructions,
      programmeId,
    }: {
      instructions?: string;
      /** Programme visé (0292) ; vide = celui en vigueur, sinon le prochain. */
      programmeId?: string;
    } = {}): Promise<number> => {
      const { data, error } = await supabase.functions.invoke('curriculum-pack-plan', {
        body: {
          level,
          subject,
          instructions: instructions?.trim() || undefined,
          programme_id: programmeId || undefined,
        },
      });
      if (error) throw new Error(error.message);
      const res = data as { created?: number; error?: string };
      if (res.error)
        throw new Error(
          res.error === 'nothing_to_plan'
            ? 'Aucun chapitre ni carte pour ce niveau.'
            : res.error === 'no_programme'
              ? 'Crée d’abord le programme officiel de ce niveau.'
              : res.error,
        );
      return res.created ?? 0;
    },
    onSuccess: () => invalidateAdmin(qc, level, subject),
  });
}
